import { BrandAvatar } from "@faire/deprecated-retailer-visitor-shared/components/BrandAvatar/index";
import { NoTranslate } from "@faire/deprecated-retailer-visitor-shared/components/NoTranslate";
import { useTopShopVisibility } from "@faire/deprecated-retailer-visitor-shared/components/TopShop/useTopShopVisibility";
import { useIsLoggedInRetailerOrBrandPreview } from "@faire/deprecated-retailer-visitor-shared/lib/isLoggedInRetailer";
import { trackBrandClick } from "@faire/deprecated-retailer-visitor-shared/services/Events";
import { getSettingElevateTopShopV2 } from "@faire/deprecated-retailer-visitor-shared/settings/getSettingElevateTopShopV2";
import { useSettingPDPUXPhase2 } from "@faire/deprecated-retailer-visitor-shared/settings/useSettingPDPUXPhase2";
import { TopShopBadge } from "@faire/deprecated-retailer-visitor-shared/views/brand/components/TopShopBadge";
import { ProductDetails } from "@faire/deprecated-retailer-visitor-shared/views/product/ProductDetails/model";
import { generateBrandUrlForProductPage } from "@faire/deprecated-retailer-visitor-shared/views/product/utils/generateBrandUrlForProductPage";
import { Core } from "@faire/design-tokens";
import { IProductPageResponse } from "@faire/web-api--source/indigofair/data/IProductPageResponse";
import { useStrictLocalization } from "@faire/web/common/localization";
import { formatMoney, moneyOrZero } from "@faire/web/common/money";
import { faireBaseUrl } from "@faire/web/common/routes/faireBaseUrl";
import { useTraceComponentRender } from "@faire/web/common/tracing/useTraceComponentRender";
import { Button } from "@faire/web/slate/Button";
import { Flex, Row } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { Link } from "@faire/web/ui/LinkAnchor";
import React from "react";
import styled from "styled-components";

import { MaybeBrandReviewSummary } from "../__internal__/Reviews/MaybeBrandReviewSummary";
import { IBrandReviewSummary } from "../hooks/interfaces";

const LONG_BRAND_NAME_LENGTH = 20;

export const MaybeSimpleBrandTitle: React.FC<{
  brand: IProductPageResponse.IPdpBrand | undefined;
  product: ProductDetails.IProduct;
  brandReviewSummary: IBrandReviewSummary;
  isDesktopAndAbove: boolean;
}> = ({ brand, product, brandReviewSummary, isDesktopAndAbove }) => {
  if (!brand) {
    return null;
  }
  return (
    <SimpleBrandTitle
      brand={brand}
      product={product}
      brandReviewSummary={brandReviewSummary}
      isDesktopAndAbove={isDesktopAndAbove}
    />
  );
};

const SimpleBrandTitle: React.FC<{
  brand: IProductPageResponse.IPdpBrand;
  product: ProductDetails.IProduct;
  brandReviewSummary: IBrandReviewSummary;
  isDesktopAndAbove: boolean;
}> = ({ brand, product, brandReviewSummary, isDesktopAndAbove }) => {
  useTraceComponentRender("SimpleBrandTitle");
  const { strictLocalize } = useStrictLocalization();
  const { isTablet } = useViewport();
  const { isTopShopContentVisibleForBrand } = useTopShopVisibility(
    brand.is_top_shop
  );

  const minimumInfo = strictLocalize(
    {
      id: "doMs11",
      defaultMessage: "{minimumOrderAmountMoney} min",
      description: {
        text: "Minimum amount spent to order from this brand. Min is an abbreviation for minimum.",
      },
    },
    {
      minimumOrderAmountMoney: formatMoney({
        money: moneyOrZero(brand.minimum_order_amount),
      }),
    }
  );

  const onBrandClick = () => {
    if (brand?.token !== undefined) {
      trackBrandClick(brand.token, "product_page_simple_brand_title");
    }
  };

  const token = product.original_brand_token ?? brand?.token;
  const brandPageUrl = token
    ? generateBrandUrlForProductPage(token, product.token ?? "")
    : faireBaseUrl();

  const brandNameLength = brand.name?.length ?? 0;

  const shouldShowBrandSummaryBelowBrandName =
    brandNameLength >= LONG_BRAND_NAME_LENGTH ||
    isTablet ||
    getSettingElevateTopShopV2();

  return (
    <Row align="center">
      <Link onClick={onBrandClick} to={brandPageUrl}>
        {brand.profile_image?.url ? (
          <>
            <BrandAvatar
              image={brand.profile_image}
              size="smaller"
              isTopShop={brand.is_top_shop}
              iconSize="22px"
              displayTooltip
              hideIcon={getSettingElevateTopShopV2()}
              hideTopShopBorder={getSettingElevateTopShopV2()}
            />
            <meta itemProp="image" content={brand.profile_image?.url} />
          </>
        ) : null}
      </Link>
      <Spacer size="1x" />
      <Wrapper
        $flexDirection={shouldShowBrandSummaryBelowBrandName ? "column" : "row"}
      >
        <Row align="center">
          <Button
            style={{ textDecoration: "none" }}
            variant="tertiary"
            onClick={onBrandClick}
            linkTo={brandPageUrl}
          >
            <NoTranslate>
              <Typography variant="paragraphSansMedium">
                {brand.name}
              </Typography>
            </NoTranslate>
          </Button>
        </Row>
        {!shouldShowBrandSummaryBelowBrandName ? (
          <>
            <Spacer size={4} />
            <Typography>{" · "}</Typography>
            <Spacer size={4} />
          </>
        ) : null}
        <Row align="center">
          {isTopShopContentVisibleForBrand && getSettingElevateTopShopV2() ? (
            <CenterAlignedRow>
              <TopShopBadge
                iconSize="12px"
                typographyVariant="labelSansRegular"
                tooltipPosition="bottom"
              />
            </CenterAlignedRow>
          ) : null}
          <MaybeBrandReviewSummary
            brandReviewSummary={brandReviewSummary}
            wrapper={CenterAlignedRow}
            typographyVariant="labelSansRegular"
            isDesktopAndAbove={isDesktopAndAbove}
          />
          <MaybeBrandMinimum minimumInfo={minimumInfo} />
        </Row>
      </Wrapper>
    </Row>
  );
};

const MaybeBrandMinimum = ({ minimumInfo }: { minimumInfo: string }) => {
  const showPDPUXPhase2 = useSettingPDPUXPhase2();
  const isLoggedInRetailerOrBrandPreview =
    useIsLoggedInRetailerOrBrandPreview();
  return isLoggedInRetailerOrBrandPreview || showPDPUXPhase2 ? (
    <CenterAlignedRow>
      <Typography variant="labelSansRegular" color={Core.text.default}>
        {minimumInfo}
      </Typography>
    </CenterAlignedRow>
  ) : null;
};

const Wrapper = styled(Flex)<{
  $flexDirection: string;
}>`
  flex-flow: ${(props) => props.$flexDirection} wrap;
`;

const CenterAlignedRow = styled(Row).attrs({
  align: "center",
})`
  & + & {
    &:before {
      content: " ";
      background-color: ${Core.surface.default};
      height: 2px;
      width: 2px;
      margin: 0 6px;
      border-radius: 50%;
    }
  }
`;
